import { Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { Button, Color, RadioGroup, Radius, Spacing, Text } from 'zens-storybook'
import { ApiError, useStore } from 'zens-store'
import { useNumberParams, useRouter } from 'zens-router'

import { CompanyHeader, LayoutHeader } from '@dms/component'
import { usePermissions } from '@dms/hook'

import { CompanySubscriptionsResponse, useCompanySubscriptions } from '../../component/company/use-subscriptions'

const SubscriptionEditor = ({ companyId }: { companyId: number }) => {
  const { t } = useTranslation()
  const { api, message } = useStore()
  const router = useRouter()
  const { isSysAdmin } = usePermissions(companyId)
  const { data, error, loading, reload } = useCompanySubscriptions(companyId, isSysAdmin)
  const [plans, setPlans] = useState<Record<string, string | null>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const submitting = useRef(false)
  const active = useRef(true)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])

  useEffect(() => {
    if (!isSysAdmin) router.replace(`/companies/${companyId}/detail`)
  }, [companyId, isSysAdmin, router])

  useEffect(() => {
    if (data) setPlans(Object.fromEntries(data.subscriptions.map((item) => [item.feature, item.plan_code])))
  }, [data])

  if (!isSysAdmin) return null
  const changes =
    data?.subscriptions
      .filter(
        (item) => Object.prototype.hasOwnProperty.call(plans, item.feature) && plans[item.feature] !== item.plan_code,
      )
      .map((item) => ({ feature: item.feature, plan_code: plans[item.feature] })) ?? []

  const save = async () => {
    if (submitting.current || !data || !isSysAdmin || !changes.length) return
    submitting.current = true
    setSaving(true)
    setSaveError(null)
    try {
      await api.patch<CompanySubscriptionsResponse>(`companies/${companyId}/subscriptions`, { subscriptions: changes })
      if (!active.current) return
      message.add({ message: t('Subscriptions were updated successfully'), status: 'success' })
      router.push(`/companies/${companyId}/detail`)
    } catch (error) {
      if (active.current) {
        const apiError = error as ApiError
        setSaveError(apiError.fields?.subscriptions || apiError.message || t('Subscriptions could not be saved'))
      }
    } finally {
      submitting.current = false
      if (active.current) setSaving(false)
    }
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <CompanyHeader
          companyId={companyId}
          title={t('Subscriptions')}
          breadcrumbItems={[{ title: t('Subscriptions') }]}
        />
      </Suspense>
      <View style={styles.content}>
        <Text>{t('Choose a plan for each feature. Changes apply to this company.')}</Text>
        {loading && <ActivityIndicator accessibilityLabel={t('Loading...')} />}
        {error && (
          <View>
            <Text style={styles.error}>{t('Subscriptions could not be loaded')}</Text>
            <Button light title={t('Retry')} onPress={reload} />
          </View>
        )}
        {data?.subscriptions.map((item) => (
          <View key={item.feature} style={styles.feature}>
            <Text level={4}>{t(item.name)}</Text>
            <RadioGroup
              items={[
                ...item.available_plans.map((plan) => ({ value: plan.code, title: t(plan.name) })),
              ]}
              value={
                (Object.prototype.hasOwnProperty.call(plans, item.feature) ? plans[item.feature] : item.plan_code) ?? ''
              }
              onSelect={(value: string) => setPlans((previous) => ({ ...previous, [item.feature]: value || null }))}
              disabled={saving || !isSysAdmin}
              accessibilityLabel={t('Plan for {{feature}}', { feature: t(item.name) })}
              testID={`CompanySubscriptions.Plan.${item.feature}`}
              style={styles.plans}
              optionStyle={styles.planOption}
            />
          </View>
        ))}
        {data && !data.subscriptions.length && <Text>{t('No subscription features available')}</Text>}
        {saveError && (
          <Text accessibilityRole="alert" style={styles.error}>
            {saveError}
          </Text>
        )}
        <View style={styles.buttons}>
          <Button
            title={t('Save')}
            onPress={save}
            left={saving ? <ActivityIndicator /> : undefined}
            disabled={!data || !isSysAdmin || !changes.length || saving}
            testID="CompanySubscriptions.Save"
          />
          <Button
            light
            title={t('Cancel')}
            disabled={saving}
            onPress={() => router.push(`/companies/${companyId}/detail`)}
            testID="CompanySubscriptions.Cancel"
          />
        </View>
      </View>
    </View>
  )
}

const CompanySubscriptionsScreen = () => {
  const [{ companyId }] = useNumberParams()
  return <SubscriptionEditor key={companyId} companyId={companyId} />
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
    gap: Spacing.L,
  },
  feature: { gap: Spacing.S },
  plans: { flexWrap: 'wrap', gap: Spacing.XS },
  planOption: { minWidth: 180, marginLeft: 0 },
  buttons: { flexDirection: 'row', gap: Spacing.S },
  error: { color: Color.darkRed },
})

export default CompanySubscriptionsScreen
